import { queryOptions } from '@tanstack/react-query'
import fetcher from '@/core/lib/fetcher'
import type { APIResponse } from '@/core/types/utils'
import type {
  BulkAssignmentPayload,
  DateRange,
  SeasonalAssignmentRow,
  SeasonalEntityKind,
} from './seasonal.utils'

// Success payloads per the openapi contract.
type BulkUpsertResult = { daysWritten: number; targets: number }
type DeleteDaysResult = {
  rateId?: string
  activityId?: string
  daysDeleted: number
}

const propertyDaysUrl = (siteId: string) =>
  import.meta.env.VITE_API_BOOKING +
  `property/${siteId}/cancellation-policy-days`

const entityDaysUrl = (kind: SeasonalEntityKind, entityId: string) =>
  import.meta.env.VITE_API_BOOKING +
  `property/${kind}/${entityId}/cancellation-policy-days`

// Prefix shared by every window of a property, so a save or delete can
// invalidate the list without knowing which window is on screen.
export const seasonalAssignmentsKey = (siteId: string) =>
  ['seasonalCancellationPolicies', siteId] as const

export const seasonalAssignmentsQueryOptions = (
  siteId: string,
  window: { from: string; to: string },
  token: string
) =>
  queryOptions({
    queryKey: [...seasonalAssignmentsKey(siteId), window.from, window.to, token],
    queryFn: () =>
      fetcher
        .auth(`Bearer ${token}`)
        .url(propertyDaysUrl(siteId))
        .query(window)
        .get()
        .json<APIResponse<SeasonalAssignmentRow[]>>(),
  })

export const bulkUpsertSeasonalAssignments = (
  siteId: string,
  payload: BulkAssignmentPayload,
  token: string
) =>
  fetcher
    .auth(`Bearer ${token}`)
    .url(propertyDaysUrl(siteId))
    .put(payload)
    .json<APIResponse<BulkUpsertResult>>()

// Row delete stays on the per-entity route: a list row is exactly one entity
// and one date range, which is what that DELETE takes.
export const deleteSeasonalAssignment = (
  kind: SeasonalEntityKind,
  entityId: string,
  range: DateRange,
  token: string
) =>
  fetcher
    .auth(`Bearer ${token}`)
    .url(entityDaysUrl(kind, entityId))
    .query({ from: range.startDate, to: range.endDate })
    .delete()
    .json<APIResponse<DeleteDaysResult>>()
